import { useMemo, useState } from "react";
import { ArrowRight, Bell, Clock, Search, Sprout, X } from "lucide-react";
import { CATEGORIES, scrollToId } from "../lib/site";
import { IMAGES } from "../lib/images";
import { FadeUp, SectionHeading } from "./Reveal";

const CATEGORY_TAGLINES = {
  spices: "Sun-dried, hand-picked flavour",
  fruits: "Orchard-fresh sweetness",
  nursery: "Green beginnings for your home",
  vegetables: "Daily-use & premium dried veggies",
};

const EXPLORE_LABELS = {
  spices: "Explore Spices",
  fruits: "Explore Fruits",
  nursery: "Explore Plants",
  vegetables: "Explore Vegetables",
};

const SECTION_META = {
  spices: {
    id: "spices",
    eyebrow: "01 — Spices",
    title: "Organic Spices From Our Farm",
    subtitle: "Authentic flavors, naturally grown.",
    bg: "bg-cream",
  },
  fruits: {
    id: "fruits",
    eyebrow: "02 — Fruits",
    title: "Fresh Organic Fruits",
    subtitle: "Freshly grown, naturally delicious.",
    bg: "bg-beige",
  },
  nursery: {
    id: "nursery",
    eyebrow: "03 — Nursery",
    title: "Bring Nature Home",
    subtitle: "Healthy plants for your garden, home and farm.",
    bg: "bg-cream",
  },
  vegetables: {
    id: "vegetables",
    eyebrow: "04 — Dehydrated Vegetables",
    title: "Indian Daily Use & Premium Vegetable Range",
    subtitle: "Farm vegetables, gently dried — ready in minutes, no chopping, no waste.",
    bg: "bg-beige",
  },
};

const VEG_GROUPS = [
  { key: "a", title: "Category A — Everyday Indian Kitchen Essentials" },
  { key: "b", title: "Category B — Premium & Superfood Indian Veggies" },
];

const NURSERY_GROUPS = [
  { key: "fruit", title: "Fruit Plants — Grow Your Own Harvest" },
  { key: "flowering", title: "Flowering & Ornamental Plants" },
];

export function CategoryCards() {
  return (
    <section id="categories" data-testid="category-cards-section" className="relative bg-nightgreen py-24 sm:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          dark
          center
          testId="category-heading"
          eyebrow="Our Harvest"
          title="Four Gifts From Our Soil"
          subtitle="From spice beds to orchard rows, the nursery and the dried-vegetable barn — explore everything the farm grows."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CATEGORIES.map((c, i) => (
            <FadeUp key={c.key} delay={i * 0.1}>
              <button
                data-testid={`category-card-${c.key}`}
                onClick={() => scrollToId(c.key)}
                className="group relative block w-full h-[340px] sm:h-[400px] rounded-2xl overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-gold"
              >
                <img
                  src={IMAGES.categories[c.key]}
                  alt={`${c.label} at Unique Organic Farm`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1300ms] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-nightgreen/95 via-nightgreen/25 to-transparent" />
                <div className="absolute inset-0 rounded-2xl border border-cream/10 transition-colors duration-500 group-hover:border-gold/60" />
                <div className="absolute top-5 left-5 font-serif text-5xl font-medium text-gold/80">{c.num}</div>
                <div className="absolute bottom-0 inset-x-0 p-6">
                  <h3 className="font-serif text-2xl text-cream tracking-wide">{c.label.toUpperCase()}</h3>
                  <p className="mt-1 text-xs text-cream/70 font-light">{CATEGORY_TAGLINES[c.key]}</p>
                  <span className="mt-3.5 inline-flex items-center gap-2 text-goldlight text-[11px] font-semibold tracking-[0.24em] uppercase">
                    {EXPLORE_LABELS[c.key]}
                    <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1.5" />
                  </span>
                </div>
              </button>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function AvailabilityBadge({ availability, slug }) {
  const meta = {
    available: { label: "Available", cls: "bg-[#EBF2EC]/95 text-forest border-[#B8D4BD]", dot: "bg-forest" },
    coming_soon: { label: "Coming Soon", cls: "bg-[#F3ECE0]/95 text-[#A87B32] border-[#DECDB3]", dot: "bg-[#A87B32]" },
    to_confirm: { label: "Availability to be confirmed", cls: "bg-[#F3ECE0]/95 text-soil border-[#DECDB3]", dot: "bg-[#A87B32]" },
  }[availability] || { label: "Available", cls: "bg-[#EBF2EC]/95 text-forest border-[#B8D4BD]", dot: "bg-forest" };
  return (
    <span
      data-testid={`availability-badge-${slug}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] backdrop-blur-sm ${meta.cls}`}
    >
      {availability === "coming_soon" ? <Sprout className="w-3 h-3" /> : <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />}
      {meta.label}
    </span>
  );
}

function ProductCard({ product, onView, onEnquire, index }) {
  const comingSoon = product.availability === "coming_soon";
  const slug = product.slug || product.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const first = product.images?.[0];
  const second = product.images?.[1] || first;

  return (
    <FadeUp delay={(index % 4) * 0.08} className="h-full">
      <article
        data-testid={`product-card-${slug}`}
        className="group h-full flex flex-col rounded-2xl overflow-hidden bg-white border border-sand transition-all duration-500 hover:shadow-[0_20px_60px_rgba(17,38,29,0.14)] hover:-translate-y-1.5"
      >
        <div
          className="relative aspect-[4/5] overflow-hidden cursor-pointer"
          onClick={() => onView(product)}
          role="button"
          aria-label={`View ${product.name}`}
        >
          {first && (
            <img
              src={first.url}
              alt={first.alt || product.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
            />
          )}
          {second && second.url !== first?.url && (
            <img
              src={second.url}
              alt={second.alt || `${product.name} alternate view`}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-nightgreen/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute top-3 left-3">
            <AvailabilityBadge availability={product.availability} slug={slug} />
          </div>
        </div>

        <div className="flex flex-col gap-1.5 p-5 grow">
          <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">{product.tag || product.category}</span>
          <h3 className="font-serif text-xl font-semibold text-forest leading-snug">{product.name}</h3>
          <p className="text-sm font-light text-muted leading-relaxed line-clamp-2">{product.description}</p>
          {product.rehydration && (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-sage">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              Rehydrates: {product.rehydration}
            </p>
          )}
          <div className="mt-auto pt-4 flex items-center gap-2.5">
            <button
              data-testid={`product-details-${slug}`}
              onClick={() => onView(product)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-forest/25 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest transition-all duration-400 hover:bg-forest hover:text-cream"
            >
              View Details
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {comingSoon ? (
              <button
                data-testid={`product-notify-${slug}`}
                onClick={() => onEnquire(product)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#A87B32] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-cream transition-all duration-400 hover:bg-gold hover:text-nightgreen"
              >
                <Bell className="w-3.5 h-3.5" />
                Notify Me
              </button>
            ) : (
              <button
                data-testid={`product-enquire-${slug}`}
                onClick={() => onEnquire(product)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-gold px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-nightgreen transition-all duration-400 hover:bg-forest hover:text-cream"
              >
                Enquire Now
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </article>
    </FadeUp>
  );
}

export function Shop({ products, onView, onEnquire }) {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  const [avail, setAvail] = useState("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (avail !== "all" && p.availability !== avail) return false;
      if (q && !p.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [products, query, cat, avail]);

  const isFiltering = query.trim() !== "" || cat !== "all" || avail !== "all";

  return (
    <div data-testid="shop-sections">
      <section className="bg-cream pt-24 sm:pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <div className="rounded-2xl border border-sand bg-white p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4 shadow-[0_10px_40px_rgba(17,38,29,0.06)]">
              <label className="relative flex-1 min-w-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  data-testid="product-search-input"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search turmeric, lemon, money plant…"
                  className="w-full rounded-full border border-sand bg-cream py-3 pl-11 pr-10 text-sm text-ink placeholder:text-muted/60 focus:outline-none focus:ring-2 focus:ring-gold/60"
                />
                {query && (
                  <button
                    data-testid="search-clear-btn"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-forest"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </label>
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Category filter">
                {[{ key: "all", label: "All" }, ...CATEGORIES.map((c) => ({ key: c.key, label: c.label }))].map((c) => (
                  <button
                    key={c.key}
                    data-testid={`filter-category-${c.key}`}
                    onClick={() => setCat(c.key)}
                    className={`rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                      cat === c.key ? "bg-forest text-cream shadow" : "bg-cream text-forest/70 border border-sand hover:border-forest/40"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
                <span className="hidden lg:block w-px h-6 bg-sand mx-1" />
                {[
                  { key: "all", label: "All" },
                  { key: "available", label: "Available" },
                  { key: "coming_soon", label: "Coming Soon" },
                ].map((a) => (
                  <button
                    key={a.key}
                    data-testid={`filter-availability-${a.key}`}
                    onClick={() => setAvail(a.key)}
                    className={`rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-all duration-300 ${
                      avail === a.key ? "bg-gold text-nightgreen shadow" : "bg-cream text-forest/70 border border-sand hover:border-gold/60"
                    }`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
          </FadeUp>
          {isFiltering && filtered.length === 0 && (
            <div data-testid="no-results" className="py-16 text-center">
              <p className="font-serif text-2xl text-forest">Nothing matches that search.</p>
              <button
                data-testid="reset-filters-btn"
                onClick={() => { setQuery(""); setCat("all"); setAvail("all"); }}
                className="mt-4 rounded-full border border-forest/30 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-forest hover:bg-forest hover:text-cream transition-all"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </section>

      {CATEGORIES.map((c) => {
        const meta = SECTION_META[c.key];
        const items = filtered.filter((p) => p.category === c.key);
        if (items.length === 0) return null;
        const groupDefs = c.key === "vegetables" ? VEG_GROUPS : c.key === "nursery" ? NURSERY_GROUPS : null;
        let groups = groupDefs
          ? groupDefs.map((g) => ({ ...g, items: items.filter((p) => p.group === g.key) })).filter((g) => g.items.length > 0)
          : [{ key: "main", title: null, items }];
        if (groupDefs) {
          const groupedIds = new Set(groups.flatMap((g) => g.items.map((p) => p.id)));
          const rest = items.filter((p) => !groupedIds.has(p.id));
          if (rest.length > 0) groups.push({ key: "rest", title: groups.length ? "More Products" : null, items: rest });
        }
        return (
          <section key={c.key} id={meta.id} data-testid={`${c.key}-section`} className={`${meta.bg} py-20 sm:py-24`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap items-end justify-between gap-6">
                <SectionHeading testId={`${c.key}-heading`} eyebrow={meta.eyebrow} title={meta.title} subtitle={meta.subtitle} />
                <FadeUp delay={0.2}>
                  <span className="font-serif italic text-lg text-muted">
                    {items.length} {items.length === 1 ? "product" : "products"}
                  </span>
                </FadeUp>
              </div>
              {groups.map((g) => (
                <div key={g.key}>
                  {g.title && (
                    <FadeUp>
                      <h3
                        data-testid={`${c.key}-group-${g.key}`}
                        className="mt-12 border-l-2 border-gold pl-4 font-serif text-xl sm:text-2xl font-semibold text-forest"
                      >
                        {g.title}
                      </h3>
                    </FadeUp>
                  )}
                  <div className={`${g.title ? "mt-7" : "mt-12"} grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-7`}>
                    {g.items.map((p, i) => (
                      <ProductCard key={p.id} product={p} index={i} onView={onView} onEnquire={onEnquire} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function ComingSoonBand({ products, onNotify }) {
  if (!products.length) return null;
  return (
    <section data-testid="coming-soon-section" className="bg-beige py-20 sm:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testId="coming-soon-heading"
          eyebrow="Next Harvest"
          title="Coming Soon to the Farm"
          subtitle="These are still growing. Leave your email and we'll let you know the moment they're ready."
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => {
            const slug = p.slug || p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
            return (
              <FadeUp key={p.id} delay={i * 0.1}>
                <div
                  data-testid={`coming-soon-card-${slug}`}
                  className="group flex items-center gap-5 rounded-2xl bg-white border border-sand p-4 transition-all duration-500 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="relative w-24 h-24 rounded-xl overflow-hidden shrink-0">
                    {p.images?.[0] && (
                      <img src={p.images[0].url} alt={p.images[0].alt || p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <AvailabilityBadge availability="coming_soon" slug={`band-${slug}`} />
                    <h3 className="mt-2 font-serif text-lg font-semibold text-forest truncate">{p.name}</h3>
                    <button
                      data-testid={`coming-soon-notify-${slug}`}
                      onClick={() => onNotify(p)}
                      className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold hover:text-forest transition-colors"
                    >
                      <Bell className="w-3.5 h-3.5" />
                      Notify Me
                    </button>
                  </div>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}
